import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileContract, faDownload, faCheck, faExclamationTriangle, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';

const TermsSettings: React.FC = () => {
  // @ts-ignore - react-i18next supports namespace parameter but TypeScript types don't reflect it
  const { t, i18n } = useTranslation('settings');
  const { business } = useAuth();
  const [activeDocument, setActiveDocument] = useState('terms');

  const documents = [
    {
      id: 'terms',
      title: t('termsSettings.userAgreement'),
      lastUpdated: '2024-01-15',
      version: 'v2.1',
      description: t('termsSettings.userAgreementDesc')
    },
    {
      id: 'privacy',
      title: t('termsSettings.privacyPolicy'),
      lastUpdated: '2024-01-10',
      version: 'v1.8',
      description: t('termsSettings.privacyPolicyDesc')
    },
    {
      id: 'cookies',
      title: t('termsSettings.cookiePolicy'),
      lastUpdated: '2024-01-05',
      version: 'v1.3',
      description: t('termsSettings.cookiePolicyDesc')
    },
    {
      id: 'refund',
      title: t('termsSettings.refundPolicy'),
      lastUpdated: '2023-12-20',
      version: 'v1.2',
      description: t('termsSettings.refundPolicyDesc')
    }
  ];

  const termsContent = `
    KULLANICI SÖZLEŞMESİ

    1. GENEL HÜKÜMLER

    1.1. Bu sözleşme, Winnomi AI platformunu kullanan tüm kullanıcılar için geçerlidir.
    1.2. Platformu kullanarak bu sözleşmeyi kabul etmiş sayılırsınız.
    1.3. Sözleşme şartları önceden haber verilmeksizin değiştirilebilir.

    2. HİZMET TANIMI

    2.1. Winnomi AI, sosyal medya analitik ve yönetim hizmetleri sunar.
    2.2. Hizmetler 7/24 erişilebilir olacak şekilde tasarlanmıştır.
    2.3. Bakım ve güncelleme durumlarında hizmet kesintileri olabilir.

    3. KULLANICI YÜKÜMLÜLÜKLERİ

    3.1. Doğru ve güncel bilgiler sağlamalısınız.
    3.2. Hesabınızın güvenliğinden sorumlusunuz.
    3.3. Platformu yasalara aykırı amaçlarla kullanamazsınız.

    4. FİKRİ MÜLKİYET

    4.1. Platform içeriği telif hakkı ile korunmaktadır.
    4.2. Kullanıcılar kendi içeriklerinden sorumludur.
    4.3. İzinsiz kopyalama ve dağıtım yasaktır.

    5. SORUMLULUK SINIRLARI

    5.1. Winnomi AI, dolaylı zararlardan sorumlu değildir.
    5.2. Hizmet kesintilerinden kaynaklanan zararlar için sorumluluk kabul edilmez.
    5.3. Kullanıcı verilerinin yedeklenmesi kullanıcının sorumluluğundadır.

    6. SÖZLEŞMENİN SONA ERMESİ

    6.1. Her iki taraf da sözleşmeyi feshedebilir.
    6.2. Sözleşme ihlali durumunda tek taraflı fesih hakkı saklıdır.
    6.3. Fesih sonrası veri silme işlemleri 30 gün içinde tamamlanır.
  `;

  const privacyContent = `
    GİZLİLİK POLİTİKASI

    1. VERİ TOPLAMA

    1.1. Kişisel bilgileriniz güvenli bir şekilde toplanır.
    1.2. Toplanan veriler: ad, e-posta, telefon, şirket bilgileri.
    1.3. sosyal medya hesap bilgileri sadece analiz amaçlı kullanılır.

    2. VERİ KULLANIMI

    2.1. Verileriniz hizmet kalitesini artırmak için kullanılır.
    2.2. Pazarlama amaçlı kullanım için ayrı onay alınır.
    2.3. Üçüncü taraflarla veri paylaşımı yapılmaz.

    3. VERİ GÜVENLİĞİ

    3.1. Tüm veriler 256-bit SSL şifreleme ile korunur.
    3.2. Düzenli güvenlik denetimleri yapılır.
    3.3. Veri ihlali durumunda 72 saat içinde bildirim yapılır.

    4. ÇEREZLER

    4.1. Web sitesi işlevselliği için gerekli çerezler kullanılır.
    4.2. Analitik çerezler anonim veri toplar.
    4.3. Çerez tercihlerinizi istediğiniz zaman değiştirebilirsiniz.

    5. KULLANICI HAKLARI

    5.1. Verilerinize erişim hakkınız vardır.
    5.2. Veri düzeltme ve silme taleplerinde bulunabilirsiniz.
    5.3. Veri işleme faaliyetlerine itiraz edebilirsiniz.
  `;

  const getDocumentContent = (id: string) => {
    switch (id) {
      case 'terms':
        return termsContent;
      case 'privacy':
        return privacyContent;
      case 'cookies':
        return 'ÇEREZ POLİTİKASI\n\n1. ÇEREZ TÜRLERİ\n1.1. Zorunlu çerezler: Site işlevselliği için gerekli\n1.2. Analitik çerezler: Kullanım istatistikleri için\n1.3. Pazarlama çerezleri: Kişiselleştirilmiş reklamlar için\n\n2. ÇEREZ YÖNETİMİ\n2.1. Tarayıcı ayarlarından çerezleri yönetebilirsiniz\n2.2. Çerezleri devre dışı bırakabilirsiniz\n2.3. Bazı özellikler çerez olmadan çalışmayabilir';
      case 'refund':
        return 'İADE VE İPTAL POLİTİKASI\n\n1. İADE KOŞULLARI\n1.1. 14 gün içinde iade talebinde bulunabilirsiniz\n1.2. Hizmet kullanımı %20\'den az olmalıdır\n1.3. İade işlemi 5-7 iş günü içinde tamamlanır\n\n2. İPTAL İŞLEMLERİ\n2.1. Abonelik iptali anında geçerli olur\n2.2. Kalan süre için orantılı iade yapılır\n2.3. İptal sonrası veriler 30 gün saklanır';
      default:
        return 'Belge içeriği yükleniyor...';
    }
  };

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">{t('termsSettings.title')}</h2>
        <p className="text-white/70">{t('termsSettings.description')}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Document List */}
        <div className="lg:col-span-1">
          <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">{t('termsSettings.legalDocuments')}</h3>
            <div className="space-y-3">
              {documents.map((doc) => (
                <button
                  key={doc.id}
                  onClick={() => setActiveDocument(doc.id)}
                  className={`w-full text-left p-4 rounded-xl transition-all duration-300 ${
                    activeDocument === doc.id
                      ? 'bg-gradient-to-r from-purple-500/20 to-purple-700/20 border border-purple-400/30'
                      : 'bg-white/5 border border-white/10 hover:bg-white/10'
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    <FontAwesomeIcon icon={faFileContract} className="text-purple-400 text-sm mt-1" />
                    <div className="flex-1">
                      <h4 className="text-white font-semibold text-sm mb-1">{doc.title}</h4>
                      <p className="text-white/60 text-xs mb-2">{doc.description}</p>
                      <div className="flex items-center justify-between text-xs text-white/50">
                        <span>{doc.version}</span>
                        <span>{new Date(doc.lastUpdated).toLocaleDateString('tr-TR')}</span>
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Document Content */}
        <div className="lg:col-span-2">
          <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-bold text-white mb-1">
                  {documents.find(doc => doc.id === activeDocument)?.title}
                </h3>
                <p className="text-white/60 text-sm">
                  {t('termsSettings.lastUpdated')}: {new Date(documents.find(doc => doc.id === activeDocument)?.lastUpdated || '').toLocaleDateString('tr-TR')}
                </p>
              </div>
              <button className="flex items-center space-x-2 px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white hover:bg-white/20 transition-colors">
                <FontAwesomeIcon icon={faDownload} className="text-sm" />
                <span>{t('termsSettings.download')}</span>
              </button>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-xl p-6 max-h-96 overflow-y-auto">
              <pre className="text-white/80 text-sm leading-relaxed whitespace-pre-wrap font-mono">
                {getDocumentContent(activeDocument)}
              </pre>
            </div>

            {/* Agreement Status */}
            <div className="mt-6 p-4 bg-gradient-to-r from-green-500/10 to-blue-500/10 border border-green-400/20 rounded-xl">
              <div className="flex items-center space-x-3">
                <FontAwesomeIcon icon={faCheck} className="text-green-400 text-lg" />
                <div>
                  <h4 className="text-white font-semibold">{t('termsSettings.agreementStatus')}</h4>
                  <p className="text-white/70 text-sm">
                    {business?.createdAt 
                      ? t('termsSettings.agreementAccepted', { 
                          date: new Date(business.createdAt).toLocaleDateString(i18n.language === 'tr' ? 'tr-TR' : 'en-US')
                        })
                      : t('termsSettings.agreementAccepted', { date: '-' })
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Important Notices */}
      <div className="mt-8">
        <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl p-6">
          <div className="flex items-start space-x-3">
            <FontAwesomeIcon icon={faInfoCircle} className="text-blue-400 text-lg mt-1" />
            <div>
              <h4 className="text-lg font-semibold text-white mb-2">{t('termsSettings.importantNotices')}</h4>
              <ul className="space-y-2 text-white/70 text-sm">
                <li>• {t('termsSettings.notice1')}</li>
                <li>• {t('termsSettings.notice2')}</li>
                <li>• {t('termsSettings.notice3')}</li>
                <li>• {t('termsSettings.notice4')}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsSettings;
